/* frontend/src/components/Texpact/MatrixView.jsx */

import React, { useState, useEffect } from 'react';
import { texpactAPI, shipmentsAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import './MatrixView.css';

const MatrixView = ({ poCode, productCode, onShipmentCreated }) => {
  const { t } = useTranslation();
  const [matrixData, setMatrixData] = useState(null);
  const [shipmentData, setShipmentData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingNavData, setLoadingNavData] = useState(false);
  const [markCompleted, setMarkCompleted] = useState(false);

  const { 
    toasts, 
    removeToast,
    toast_success,
    toast_error,
    toast_warning,
    toast_info,
    toast_success_confirm,
    toast_error_confirm,
    toast_warning_confirm,
    toast_info_confirm
  } = useToast();  

  useEffect(() => {
    loadMatrixData();
  }, [poCode, productCode]);

  const loadMatrixData = async () => {
    try {
      setLoading(true);
      const response = await texpactAPI.getMatrix(poCode, productCode);
      setMatrixData(response.data);
      setShipmentData({});
      setMarkCompleted(false);
    } catch (err) {
      console.error('Failed to load matrix:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQtyChange = (color, size, value) => {
    const key = `${color}-${size}`;
    const numValue = parseFloat(value) || 0;
    const maxQty = matrixData.matrix[color][size].qty_pending;
    
    setShipmentData(prev => ({
      ...prev,
      [key]: Math.min(Math.max(0, numValue), maxQty)
    }));
  };

  const handleLoadNavQuantities = async () => {
    try {
      setLoadingNavData(true);
      

      console.log("poCode: ", poCode);
      const response = await texpactAPI.getNavReceivedQuantities(poCode);


      
      if (response.data.success) {
        const { matchedQuantities, stats } = response.data.data;
        
        console.log('📊 NAV Data Stats:', stats);
        
        // Update shipmentData with NAV quantities
        const newShipmentData = {};
        
        matchedQuantities.forEach(match => {
          if (match.matched && match.qty_to_ship > 0) {
            const key = `${match.color_code}-${match.size_code}`;
            newShipmentData[key] = match.qty_to_ship;
          }
        });
        
        setShipmentData(newShipmentData);
        
        // Show success message
        /*
        alert(`✅ Loaded NAV data:\n` +
              `Total NAV records: ${stats.totalNavRecords}\n` +
              `Matched lines: ${stats.matchedLines}\n` +
              `Unmatched lines: ${stats.unmatchedLines}`);
        */
      }
      
    } catch (error) {
      console.error('Error loading NAV quantities:', error);
      alert('Failed to load NAV shipment data: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingNavData(false);
    }
  };

  const handleSaveShipment = async () => {
    try {
      setSaving(true);
      
      const lines = [];
      Object.entries(shipmentData).forEach(([key, qty]) => {
        if (qty > 0) {
          const [color, size] = key.split('-');
          const cellData = matrixData.matrix[color][size];
          
          lines.push({
            line_number: cellData.line_number,
            product_code: productCode,
            description: `${productCode} - ${color} - ${size}`,
            color_code: color,
            size_code: size,
            uom: 'PZ',
            qty_shipped: qty,
            price: cellData.price
          });
        }
      });

      if (lines.length === 0 && !markCompleted) {
        toast_warning(t('orders.enter_quantity', 'Please enter at least one shipment quantity'));
        return;
      }

      const shipmentPayload = {
        poCode:          poCode,
        shipmentDate:    new Date().toISOString().split('T')[0],
        lines:           lines,
        markCompleted:   markCompleted,
        notes:           markCompleted
          ? `${t('orders.marked_complete', 'Terminado')} — ${productCode}`
          : `${t('orders.shipment_for', 'Shipment for')} ${productCode}`,
        createdBy:       'system'
      };

      await shipmentsAPI.create(shipmentPayload);
      //alert(t('orders.shipment_created', 'Shipment created successfully!'));   

      // Requires OK
      toast_success(t('orders.shipment_created', 'Shipment created successfully!'));      
      
      await loadMatrixData();
      if (onShipmentCreated) onShipmentCreated();
      
    } catch (err) {
      // Use backend's readable message (400 userError) or fall back to generic
      const msg = err.response?.data?.error
        || t('orders.shipment_error', 'Failed to create shipment');
      toast_error_confirm(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">{t('loading', 'Loading...')}</div>;
  if (!matrixData) return <div className="error">{t('no_data', 'No data available')}</div>;

  const { matrix, colors, sizes } = matrixData;

  const getTotalForSize = (size) => {
    return colors.reduce((sum, color) => {
      const cell = matrix[color]?.[size];
      return sum + (cell ? parseFloat(cell.qty_pending) : 0);
    }, 0);
  };

  const getTotalForColor = (color) => {
    return sizes.reduce((sum, size) => {
      const cell = matrix[color]?.[size];
      return sum + (cell ? parseFloat(cell.qty_pending) : 0);
    }, 0);
  };

  return (
    <div className="matrix-view">
      <h3>{t('orders.product', 'Product')}: {productCode}</h3>
      
      <div className="matrix-container">
        <table className="matrix-table">
          <thead>
            <tr>
              <th className="header-cell">{t('orders.color_size', 'Color / Size')}</th>
              {sizes.map(size => (
                <th key={size} className="size-header">{size}</th>
              ))}
              <th className="total-header">{t('total', 'Total')}</th>
            </tr>
          </thead>
          <tbody>
            {colors.map(color => (
              <tr key={color}>
                <td className="color-cell">{color}</td>
                {sizes.map(size => {
                  const cell = matrix[color]?.[size];
                  const key = `${color}-${size}`;
                  
                  if (!cell) {
                    return <td key={size} className="empty-cell">-</td>;
                  }

                  return (
                    <td key={size} className="data-cell">
                      <div className="cell-content">
                        <div className="qty-info">
                          <span className="label">{t('orders.qty', 'Qty')}:</span>
                          <span className="value">{cell.qty.toFixed(0)}</span>
                        </div>
                        <div className="qty-info">
                          <span className="label">{t('orders.pending', 'Pending')}:</span>
                          <span className="value pending">{cell.qty_pending.toFixed(0)}</span>
                        </div>
                        <div className="qty-info">
                          <span className="label">{t('orders.shipped', 'Shipped')}:</span>
                          <span className="value shipped">{cell.qty_shipped.toFixed(0)}</span>
                        </div>
                        <div className="shipment-input">
                          <label>{t('orders.ship_now', 'Ship Now')}:</label>
                          <input
                            type="number"
                            min="0"
                            max={cell.qty_pending}
                            value={shipmentData[key] || ''}
                            onChange={(e) => handleQtyChange(color, size, e.target.value)}
                            className="qty-input"
                            disabled={cell.qty_pending <= 0}
                          />
                        </div>
                      </div>
                    </td>
                  );
                })}
                <td className="total-cell">{getTotalForColor(color).toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="footer-cell">{t('total', 'Total')}</td>
              {sizes.map(size => (
                <td key={size} className="total-cell">
                  {getTotalForSize(size).toFixed(0)}
                </td>
              ))}
              <td className="grand-total">
                {colors.reduce((sum, color) => sum + getTotalForColor(color), 0).toFixed(0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Actions Section - No date input */}
      <div className="matrix-actions">
        <div className="left-actions">
          <button
            onClick={handleLoadNavQuantities}
            className="btn-secondary"
            disabled={loadingNavData || saving}
          >
            {loadingNavData ? '⏳ Loading NAV Data...' : '📥 Load from NAV'}
          </button>
        </div>
        
        <div className="right-actions">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600, cursor: 'pointer', userSelect: 'none',
            color: markCompleted ? 'var(--primary)' : 'var(--gray-600)' }}>
            <input
              type="checkbox"
              checked={markCompleted}
              onChange={e => setMarkCompleted(e.target.checked)}
              style={{ width: 15, height: 15, accentColor: 'var(--primary)', cursor: 'pointer' }}
            />
            Terminado
          </label>

          <button
            onClick={() => setShipmentData({})}
            className="btn-clear"
            disabled={saving || (Object.keys(shipmentData).length === 0 && !markCompleted)}
          >
            {t('clear', 'Clear')}
          </button>
          
          <button
            onClick={handleSaveShipment}
            disabled={saving || (Object.keys(shipmentData).length === 0 && !markCompleted)}
            className="btn-save"
          >
            {saving ? t('saving', 'Saving...') : t('orders.create_shipment', 'Create Shipment')}
          </button>
        </div>        
      </div>

      {/* Toast container */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast key={toast.id} {...toast} onClose={removeToast} />
        ))}
      </div>


    </div>
  );
};

export default MatrixView;